import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, pluck, shareReplay, tap, throttleTime } from 'rxjs/operators';
import { SnackBarService } from '../shared/snackbar.service';
import {
  Alert,
  BonusEvent,
  DriversHitchCalendarDay,
  DriversHitchStats,
  DriverTransferRequest,
  FracActivityReportPerRegion,
  FracActivityReportPerRegionWithQualifiedInfo,
  LastSettlementFees,
  OwnerDashboardStats,
  PointOfInterest,
  SHBannerLoadsCount,
  TierFeedsResponse,
  TodayDemand,
  TourRevenue, UserTierDetail, UserTierEvent,
} from '../types';
import { isClientError, isHttpErrorResponse } from '../utilities/isHttpErrorResponse';
import { SimpleFinn } from '../utilities/simpleFinn';

@Injectable({
  providedIn: 'root',
})
export class OwnerDashboardService {
  private stats$$ = new BehaviorSubject<OwnerDashboardStats>(null);
  private statsThrottle$$ = new Subject();
  private statsShared$ = this.stats$$.pipe(shareReplay(1));

  public get stats$(): Observable<OwnerDashboardStats> {
    this.statsThrottle$$.next();
    return this.statsShared$;
  }

  private todayDemand$$ = new BehaviorSubject<TodayDemand[]>(null);
  private todayDemandThrottle$$ = new Subject();
  private todayDemandShared$ = this.todayDemand$$.pipe(shareReplay(1));

  public get todayDemand$(): Observable<TodayDemand[]> {
    this.todayDemandThrottle$$.next();
    return this.todayDemandShared$;
  }

  private fracsActivity$$ = new BehaviorSubject<FracActivityReportPerRegion[]>(null);
  private fracsActivityThrottle$$ = new Subject();
  private fracsActivityShared$ = this.fracsActivity$$.pipe(shareReplay(1));

  public get fracsActivity$(): Observable<FracActivityReportPerRegion[]> {
    this.fracsActivityThrottle$$.next();
    return this.fracsActivityShared$;
  }

  private fracsActivityWithQualifiedInfo$$ = new BehaviorSubject<FracActivityReportPerRegionWithQualifiedInfo>(null);
  private fracsActivityWithQualifiedInfoThrottle$$ = new Subject();
  private fracsActivityWithQualifiedInfoShared$ = this.fracsActivityWithQualifiedInfo$$.pipe(shareReplay(1));

  public get fracsActivityWithQualifiedInfo$(): Observable<FracActivityReportPerRegionWithQualifiedInfo> {
    this.fracsActivityWithQualifiedInfoThrottle$$.next();
    return this.fracsActivityWithQualifiedInfoShared$;
  }

  private tierFeeds$$ = new BehaviorSubject<TierFeedsResponse>(null);
  private tierFeedsThrottle$$ = new Subject();
  private tierFeedsShared$ = this.tierFeeds$$.pipe(shareReplay(1));
  private tierDrivers$$ = new BehaviorSubject<UserTierDetail[]>(null);
  private tierDriversThrottle$$ = new Subject();
  private tierDriversShared$ = this.tierDrivers$$.pipe(shareReplay(1));

  public get tierFeeds$(): Observable<TierFeedsResponse> {
    this.tierFeedsThrottle$$.next();
    return this.tierFeedsShared$;
  }

  public get tierDrivers$(): Observable<UserTierDetail[]> {
    this.tierDriversThrottle$$.next();
    return this.tierDriversShared$;
  }

  private bonusEvent$$ = new BehaviorSubject<BonusEvent[]>([]);
  private bonusEventThrottle$$ = new Subject();
  private bonusEventShared$ = this.bonusEvent$$.pipe(shareReplay(1));

  public get bonusEvents$(): Observable<BonusEvent[]> {
    this.bonusEventThrottle$$.next();
    return this.bonusEventShared$;
  }

  private lastFinesAndFees$$ = new BehaviorSubject<LastSettlementFees>(null);
  private lastFinesAndFeesThrottle$$ = new Subject();
  private lastFinesAndFeesShared$ = this.lastFinesAndFees$$.pipe(shareReplay(1));

  public get lastFinesAndFeesThrottle$(): Observable<LastSettlementFees> {
    this.lastFinesAndFeesThrottle$$.next();
    return this.lastFinesAndFeesShared$;
  }

  private driversHitchStats$$ = new BehaviorSubject<DriversHitchStats>(null);
  private driversHitchStatsThrottle$$ = new Subject();
  private driversHitchStatsShared$ = this.driversHitchStats$$.pipe(shareReplay(1));

  public get driversHitchStats$(): Observable<DriversHitchStats> {
    this.driversHitchStatsThrottle$$.next();
    return this.driversHitchStatsShared$;
  }

  private driversHitchCalendar$$ = new BehaviorSubject<DriversHitchCalendarDay[]>(null);
  private driversHitchCalendarThrottle$$ = new Subject();
  private driversHitchCalendarShared$ = this.driversHitchCalendar$$.pipe(shareReplay(1));

  public get driversHitchCalendar$(): Observable<DriversHitchCalendarDay[]> {
    this.driversHitchCalendarThrottle$$.next();
    return this.driversHitchCalendarShared$;
  }

  private alerts$$ = new BehaviorSubject<Alert[]>(null);
  private alertsThrottle$$ = new Subject();
  private alertsShared$ = this.alerts$$.pipe(shareReplay(1));

  public get alerts$(): Observable<Alert[]> {
    this.alertsThrottle$$.next();
    return this.alertsShared$;
  }

  private shBannerLoadsCount$$ = new BehaviorSubject<SHBannerLoadsCount>(null);
  private shBannerLoadsCountThrottle$$ = new Subject();
  private shBannerLoadsCountShared$ = this.shBannerLoadsCount$$.pipe(shareReplay(1));

  public get shBannerLoadsCount$(): Observable<SHBannerLoadsCount> {
    this.shBannerLoadsCountThrottle$$.next();
    return this.shBannerLoadsCountShared$;
  }

  private pointOfInterests$$ = new BehaviorSubject<PointOfInterest[]>(null);
  private pointOfInterestsThrottle$$ = new Subject();
  private pointOfInterestsShared$ = this.pointOfInterests$$.pipe(shareReplay(1));

  public get pointOfInterests$(): Observable<PointOfInterest[]> {
    this.pointOfInterestsThrottle$$.next();
    return this.pointOfInterestsShared$;
  }

  private tourRevSF: SimpleFinn<TourRevenue>;

  public get tourRevenue$(): Observable<TourRevenue> {
    return this.tourRevSF.get$();
  }

  constructor(private httpClient: HttpClient, private snackbar: SnackBarService) {
    this.statsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDashboardStats();
    });

    this.todayDemandThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadTodayDemand();
    });

    this.fracsActivityThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadFracsActivity();
    });

    this.tierFeedsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadTierFeeds();
    });

    this.tierDriversThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadTierDrivers();
    });

    this.bonusEventThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadBonusEvents();
    });

    this.lastFinesAndFeesThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadFinesAndFees();
    });

    this.driversHitchStatsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDriversHitchStats();
    });

    this.driversHitchCalendarThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDriversHitchCalendar();
    });
    this.alertsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadAlerts();
    });

    this.shBannerLoadsCountThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadSHBannerLoadsCount();
    });

    this.fracsActivityWithQualifiedInfoThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadFracsActivityWithQualifiedInfo();
    });

    this.pointOfInterestsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadPointOfInterests();
    });

    this.tourRevSF = new SimpleFinn<TourRevenue>(null, this.loadTourRevenue.bind(this));
  }

  private loadPointOfInterests() {
    this.httpClient
      .get<{ pointOfInterests: PointOfInterest[] }>(`${environment.api}/v2/admin/point_of_interest_all`)
      .subscribe((resp) => {
        this.pointOfInterests$$.next(resp?.pointOfInterests || ([] as PointOfInterest[]));
      });
  }

  private async loadTourRevenue(): Promise<TourRevenue> {
    return this.httpClient.get<TourRevenue>(`${environment.api}/v2/admin/tour_dashboard_stats`).toPromise();
  }

  private loadDashboardStats() {
    this.httpClient.get<OwnerDashboardStats>(`${environment.api}/v2/admin/owner_dashboard_stats`).subscribe((stats) => {
      this.stats$$.next(stats || ({} as OwnerDashboardStats));
    });
  }

  private loadTierFeeds() {
    this.httpClient.get<TierFeedsResponse>(`${environment.api}/v2/admin/driver_tier_feeds`).subscribe((resp) => {
      this.tierFeeds$$.next(resp || ({} as TierFeedsResponse));
    });
  }

  public getDriverTierEvents$(driverID: string): Observable<UserTierEvent[]> {
    return this.httpClient.get<UserTierEvent[]>(`${environment.api}/v2/admin/driver_tier_events/${driverID}`);
  }

  private loadTierDrivers() {
    this.httpClient.get<UserTierDetail[]>(`${environment.api}/v2/admin/all_tier_driver`).subscribe((resp) => {
      this.tierDrivers$$.next(resp || ([] as UserTierDetail[]));
    });
  }

  private loadTodayDemand() {
    this.httpClient
      .get<{ data: TodayDemand[] }>(`${environment.api}/v2/admin/today_demand`)
      .subscribe((todatDemand) => {
        this.todayDemand$$.next(todatDemand?.data || ([] as TodayDemand[]));
      });
  }

  private loadFracsActivity() {
    this.httpClient
      .get<FracActivityReportPerRegion[]>(`${environment.api}/v2/admin/fracs_activity_report`)
      .subscribe((fracsActivity) => {
        const sortedFracsActivity = (fracsActivity || []).sort((a, b) =>
          a.regionName.localeCompare(b.regionName)
        );
        this.fracsActivity$$.next(sortedFracsActivity);
      });
  }

  private loadFracsActivityWithQualifiedInfo() {
    this.httpClient
      .get<FracActivityReportPerRegionWithQualifiedInfo>(
        `${environment.api}/v2/admin/fracs_activity_report_with_qualified_info`,
      )
      .subscribe((fracsActivity) => {
        this.fracsActivityWithQualifiedInfo$$.next(
          fracsActivity || ({} as FracActivityReportPerRegionWithQualifiedInfo),
        );
      });
  }

  private loadSHBannerLoadsCount() {
    this.httpClient
      .get<SHBannerLoadsCount>(`${environment.api}/v2/admin/sh_banner_loads_count`)
      .subscribe((loadsCount) => {
        this.shBannerLoadsCount$$.next(loadsCount || ({} as SHBannerLoadsCount));
      });
  }

  private async loadBonusEvents() {
    const events = await this.httpClient
      .get<{ events: BonusEvent[] }>(`${environment.api}/v2/admin/upcoming_bonus_events`)
      .pipe(map((value) => value.events))
      .toPromise();
    this.bonusEvent$$.next(events);
  }

  private async loadFinesAndFees() {
    const events = await this.httpClient
      .get<LastSettlementFees>(`${environment.api}/v2/admin/last_settlement_fines_and_fees`)
      .toPromise();
    this.lastFinesAndFees$$.next(events);
  }

  private loadDriversHitchStats() {
    this.httpClient.get<DriversHitchStats>(`${environment.api}/v2/admin/drivers_hitch_stats`).subscribe((stats) => {
      this.driversHitchStats$$.next(stats || ({} as DriversHitchStats));
    });
  }

  private loadDriversHitchCalendar() {
    let params;
    params = new HttpParams().set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    this.httpClient
      .get<DriversHitchCalendarDay[]>(`${environment.api}/v2/admin/drivers_hitch_calendar`, { params })
      .subscribe((calendarDays) => {
        this.driversHitchCalendar$$.next(calendarDays || ([] as DriversHitchCalendarDay[]));
      });
  }

  private loadAlerts() {
    this.httpClient.get<Alert[]>(`${environment.api}/v2/admin/alerts`).subscribe((alerts) => {
      this.alerts$$.next(alerts || ([] as Alert[]));
    });
  }

  public dismissAlerts$(ids: number[]): Observable<any> {
    return this.httpClient
      .post(`${environment.api}/v2/admin/dismiss_alerts`, { ids })
      .pipe(tap(() => this.alertsThrottle$$.next()));
  }

  public dismissAlertLocally(id: number) {
    this.alerts$$.next(this.alerts$$.value.filter((alert) => alert.id !== id));
  }

  public createDriverTransferRequests$(body: DriverTransferRequest[]): Observable<any> {
    return this.httpClient.post(`${environment.api}/v2/admin/driver_transfer_request`, body);
  }

  public clearAllAlerts$(): Observable<any> {
    return this.httpClient
      .post(`${environment.api}/v2/admin/dismiss_alerts`, { ids: this.alerts$$.value.map((alert) => alert.id) })
      .pipe(tap(() => this.alerts$$.next([])));
  }

  public async getExport(start?: string, end?: string): Promise<string | boolean> {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const resp = await this.httpClient
        .get<{ url: string }>(
          `${environment.api}/v2/admin/tours_export?timezone=${timezone}&startAt=${start}&endAt=${end}`,
        )
        .pipe(pluck('url'))
        .toPromise();
      return resp;
    } catch (err) {
      if (isHttpErrorResponse(err)) {
        if (isClientError(err)) {
          this.snackbar.showError(err.messageToUser);
        } else {
          this.snackbar.showError('Failed to get export');
        }
      } else {
        this.snackbar.showError('Failed to get export');
      }
      return false;
    }
  }
}
